import { useEffect, useState } from "react";
import api, { fmtUsd, formatErr } from "../lib/api";
import { Button } from "./ui/button";
import { X, ArrowCircleDown } from "@phosphor-icons/react";
import { toast } from "sonner";

const NETWORKS = ["TRC20", "ERC20", "BEP20", "SOLANA"];

const ADDR_HINT = {
  TRC20: "T... (34 chars)",
  ERC20: "0x... (42 chars)",
  BEP20: "0x... (42 chars)",
  SOLANA: "Base58 (32-44 chars)",
};

function statusBadge(status) {
  const map = {
    PENDING:   ["bg-[#FCD535]/15", "text-[#FCD535]", "Pending"],
    COMPLETED: ["bg-[#0ECB81]/15", "text-[#0ECB81]", "Completed"],
    REJECTED:  ["bg-[#F6465D]/15", "text-[#F6465D]", "Rejected"],
    CANCELLED: ["bg-[#9CA3AF]/15", "text-[#9CA3AF]", "Cancelled"],
  };
  const [bg, color, label] = map[status] || map.PENDING;
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold ${bg} ${color}`}>
      {label}
    </span>
  );
}

export default function WithdrawPanel({ balance, onChanged, controlledOpen, onOpenChange, hideTrigger = false }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = (v) => { onOpenChange ? onOpenChange(v) : setInternalOpen(v); };
  const [amount, setAmount] = useState("");
  const [network, setNetwork] = useState("TRC20");
  const [address, setAddress] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const load = async () => {
    try {
      const r = await api.get("/withdraw/my");
      setHistory(r.data);
    } catch { /* empty */ }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    if (amt > balance) return toast.error("Amount exceeds available balance");
    if (!address.trim()) return toast.error("Enter wallet address");
    setBusy(true);
    try {
      await api.post("/withdraw", { amount: amt, network, address: address.trim() });
      toast.success("Withdrawal request created");
      setAmount(""); setAddress("");
      setOpen(false);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this withdrawal request? Balance will be refunded.")) return;
    try {
      await api.post("/withdraw/cancel", { id });
      toast.success("Request cancelled");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  return (
    <>
      {!hideTrigger && (
        <Button
          onClick={() => setOpen(true)}
          data-testid="open-withdraw-button"
          variant="outline"
          className="rounded-sm border-[#1F2937] hover:bg-[#1F2937] text-[#9CA3AF] hover:text-white h-9 px-3"
        >
          <ArrowCircleDown size={16} className="sm:mr-2" />
          <span className="hidden sm:inline">Withdraw</span>
        </Button>
      )}

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => !busy && setOpen(false)}
          data-testid="withdraw-modal-backdrop"
        >
          <div
            className="bg-[#0A0E17] border border-[#1F2937] rounded-sm w-full max-w-md p-5 sm:p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="withdraw-modal"
          >
            <div className="flex items-center justify-between mb-5">
              <div>
                <p className="overline">Withdraw</p>
                <h2 className="text-xl font-semibold mt-1">Request withdrawal</h2>
              </div>
              <button onClick={() => !busy && setOpen(false)} className="text-[#9CA3AF] hover:text-white" data-testid="close-withdraw-button">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="overline mb-1.5 block">Amount (USDT)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full bg-[#111827] border border-[#1F2937] focus:border-[#FCD535] outline-none px-3 py-2 mono text-sm"
                  placeholder="0.00"
                  data-testid="withdraw-amount-input"
                  required
                />
                <div className="flex items-center justify-between text-[11px] text-[#9CA3AF] mt-1.5">
                  <span>Available: <span className="mono text-white">${fmtUsd(balance)}</span></span>
                  <button type="button" onClick={() => setAmount(String(balance))} className="text-[#FCD535] hover:underline">
                    Max
                  </button>
                </div>
              </div>

              <div>
                <label className="overline mb-1.5 block">Network</label>
                <div className="grid grid-cols-4 gap-1.5">
                  {NETWORKS.map((n) => (
                    <button
                      type="button"
                      key={n}
                      onClick={() => setNetwork(n)}
                      data-testid={`network-${n}`}
                      className={`text-xs py-2 border transition-colors ${
                        network === n
                          ? "border-[#FCD535] bg-[#FCD535]/10 text-[#FCD535]"
                          : "border-[#1F2937] text-[#9CA3AF] hover:border-[#374151]"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="overline mb-1.5 block">USDT Wallet Address</label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="w-full bg-[#111827] border border-[#1F2937] focus:border-[#FCD535] outline-none px-3 py-2 mono text-xs sm:text-sm"
                  placeholder={ADDR_HINT[network]}
                  data-testid="withdraw-address-input"
                  required
                />
                <div className="text-[11px] text-[#9CA3AF] mt-1.5">
                  Make sure the address matches the <span className="text-white font-semibold">{network}</span> network. Sending to wrong network = funds lost.
                </div>
              </div>

              <div className="bg-[#111827] border border-[#1F2937] p-3 space-y-1 text-xs">
                <div className="flex justify-between"><span className="text-[#9CA3AF]">You send</span><span className="mono">${fmtUsd(parseFloat(amount) || 0)}</span></div>
                <div className="flex justify-between"><span className="text-[#9CA3AF]">Network fee</span><span className="mono text-[#0ECB81]">FREE</span></div>
                <div className="flex justify-between font-semibold border-t border-[#1F2937] pt-1.5 mt-1.5"><span>You receive</span><span className="mono">${fmtUsd(parseFloat(amount) || 0)}</span></div>
              </div>

              <Button
                type="submit"
                disabled={busy}
                data-testid="submit-withdraw-button"
                className="w-full bg-[#FCD535] hover:bg-[#FCD535]/90 text-black font-semibold rounded-sm h-10"
              >
                {busy ? "Submitting…" : "Submit Request"}
              </Button>
              <p className="text-[10px] text-[#9CA3AF] text-center">
                Admin will review your request and process it shortly.
              </p>
            </form>

            {history.length > 0 && (
              <div className="mt-6 pt-5 border-t border-[#1F2937]">
                <p className="overline mb-3">Your requests</p>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {history.map((w) => (
                    <div key={w.id} className="bg-[#111827] border border-[#1F2937] p-2.5 text-xs" data-testid={`withdraw-row-${w.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="mono font-semibold">${fmtUsd(w.amount)}</span>
                        {statusBadge(w.status)}
                      </div>
                      <div className="flex items-center justify-between text-[10px] text-[#9CA3AF]">
                        <span>{w.network}</span>
                        <span className="mono truncate ml-2 max-w-[140px]">{w.address}</span>
                      </div>
                      {w.status === "PENDING" && (
                        <button onClick={() => cancel(w.id)} className="mt-2 text-[10px] text-[#F6465D] hover:underline">
                          Cancel
                        </button>
                      )}
                      {w.admin_note && (
                        <div className="mt-1 text-[10px] text-[#9CA3AF] italic">Note: {w.admin_note}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
